import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { teamMembers } from '@/mocks/team';

const FEATURES = [
  { icon: 'ri-award-fill', color: 'bg-blue-600', key: '1' },
  { icon: 'ri-timer-flash-fill', color: 'bg-blue-600', key: '2' },
  { icon: 'ri-shield-check-fill', color: 'bg-blue-600', key: '3' },
  { icon: 'ri-coins-line', color: 'bg-blue-600', key: '4' },
  { icon: 'ri-customer-service-2-fill', color: 'bg-blue-600', key: '5' },
  { icon: 'ri-tools-fill', color: 'bg-blue-600', key: '6' },
];

export default function WhyUsSection() {
  const { t } = useTranslation();

  return (
    <>
      {/* ─── Why Us ─── */}
      <section className="py-12 md:py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-flex items-center gap-1.5 bg-white border border-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
              <i className="ri-star-fill text-blue-500 text-xs"></i>
              {t('whyus.badge')}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('whyus.title')} <span className="text-blue-600">{t('whyus.highlight')}</span>
            </h2>
            <p className="text-gray-500 text-sm md:text-base max-w-lg mx-auto">{t('whyus.subtitle')}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {FEATURES.map((f) => (
              <div key={f.key} className="bg-white rounded-2xl p-4 md:p-5 flex flex-col gap-3 border border-blue-50 hover:border-blue-200 transition-all">
                <span className={`${f.color} w-10 h-10 flex items-center justify-center rounded-xl shrink-0`}>
                  <i className={`${f.icon} text-white text-lg`}></i>
                </span>
                <div>
                  <p className="font-bold text-gray-900 text-sm mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>{t(`feat.${f.key}.title`)}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{t(`feat.${f.key}.desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Team ─── */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
              <i className="ri-group-fill text-blue-500 text-xs"></i>
              {t('team.badge')}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('team.title')} <span className="text-blue-600">{t('team.highlight')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {teamMembers.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-blue-200 transition-all">
                <div className="w-full h-52 sm:h-48 md:h-56 overflow-hidden">
                  <img src={m.avatar} alt={m.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-4">
                  <p className="font-extrabold text-gray-900 text-base mb-0.5" style={{ fontFamily: 'Poppins, sans-serif' }}>{m.name}</p>
                  <p className="text-blue-600 text-xs font-semibold mb-1">{t(`team.role.${m.id}`)}</p>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3">{t(`team.desc.${m.id}`)}</p>
                  <p className="text-gray-700 text-sm font-semibold mb-3">{m.phone}</p>
                  <div className="flex gap-2">
                    <a
                      href={m.phoneHref}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-phone-fill"></i> Ara
                    </a>
                    <a
                      href={m.whatsapp}
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white text-sm font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line"></i> WA
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/bizim"
              className="inline-flex items-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-bold text-sm px-8 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              {t('team.more')} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── CTA Banner ─── */}
      <section className="py-12 md:py-16 bg-blue-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('cta.title')}
          </h2>
          <p className="text-blue-200 text-sm md:text-base mb-7 max-w-lg mx-auto">{t('cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a
              href="tel:+905076074165"
              className="flex items-center justify-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-extrabold text-base px-7 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i> 0507 607 4165
            </a>
            <a
              href="https://wa.me/905076074165?text=Merhaba%2C%20tesisat%20konusunda%20yard%C4%B1m%20istiyorum."
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-extrabold text-base px-7 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i> WhatsApp ile Yaz
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
